import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Loader2 } from 'lucide-react';
import WireGuardConfig from '../../../components/edges/WireGuardConfig';
import WireGuardPeers from '../../../components/edges/WireGuardPeers';
import WireGuardAppServerConfig from '../../../components/edges/WireGuardAppServerConfig';
import FeatureDisabledBanner from '../../../components/common/FeatureDisabledBanner';
import { useSettingsStore } from '../../../stores/settingsStore';
import { useTenantStore } from '../../../stores/tenantStore';
import api from '../../../services/api';

interface WireGuardStatus {
  configured: boolean;
  enabled?: boolean;
  totalPeers?: number;
  connectedPeers?: number;
}

const WireGuard = () => {
  const { id: edgeId } = useParams<{ id: string }>();
  const { isFeatureEnabled } = useSettingsStore();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [status, setStatus] = useState<WireGuardStatus>({ configured: false });
  const [loading, setLoading] = useState(true);
  const [serverNetwork, setServerNetwork] = useState<string | undefined>();

  const fetchStatus = async () => {
    if (!edgeId) return;

    try {
      const [statusRes, configRes] = await Promise.all([
        api.get(`/api/v2/edges/${edgeId}/wireguard/status`).catch(() => ({ data: { configured: false } })),
        api.get(`/api/v2/edges/${edgeId}/wireguard/config`).catch(() => ({ data: { configured: false } })),
      ]);

      setStatus(statusRes.data);
      if (configRes.data?.config?.interfaceAddress) {
        setServerNetwork(configRes.data.config.interfaceAddress);
      }
    } catch (err) {
      console.error('Failed to load WireGuard status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();

    // Set up polling for real-time status updates (every 10 seconds)
    const interval = setInterval(async () => {
      if (!edgeId) return;

      try {
        const statusRes = await api.get(`/api/v2/edges/${edgeId}/wireguard/status`);
        setStatus(statusRes.data);
      } catch (err) {
        console.error('Failed to poll WireGuard status:', err);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [edgeId, selectedTenantId]);

  const handleConfigChange = () => {
    fetchStatus();
  };

  if (!edgeId) {
    return (
      <div className="text-center py-12 text-gray-500">
        Invalid edge ID
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading WireGuard configuration...</span>
      </div>
    );
  }

  // Feature flag gate - show upsell banner if WireGuard IoT is disabled for this tenant
  if (!isFeatureEnabled('wireguard_iot')) {
    return (
      <div className="space-y-8">
        <div className="border-b border-gray-200 pb-4">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-gray-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">WireGuard IoT Gateway</h1>
              <p className="text-sm text-gray-500">
                Configure WireGuard VPN server for IoT gateway connections
              </p>
            </div>
          </div>
        </div>
        <FeatureDisabledBanner featureKey="wireguard_iot" featureName="WireGuard IoT Gateway" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3">
          <Shield className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WireGuard IoT Gateway</h1>
            <p className="text-sm text-gray-500">
              Configure WireGuard VPN server for IoT gateway connections (GL.iNet Opal, etc.)
            </p>
          </div>
        </div>

        {status.configured && status.enabled && (
          <div className="mt-4 flex items-center space-x-4 text-sm">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Server Active
            </span>
            <span className="text-gray-500">
              {status.totalPeers || 0} peers configured
              {status.connectedPeers ? ` (${status.connectedPeers} connected)` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Server Configuration */}
      <section>
        <WireGuardConfig edgeId={edgeId} onConfigChange={handleConfigChange} />
      </section>

      {/* Peer Management */}
      <section className="pt-4 border-t border-gray-200">
        <WireGuardPeers
          edgeId={edgeId}
          configured={status.configured}
          serverNetwork={serverNetwork}
        />
      </section>

      {/* App Server Configuration (wg1) */}
      <section className="pt-8 border-t border-gray-200">
        <WireGuardAppServerConfig edgeId={edgeId} onConfigChange={handleConfigChange} />
      </section>

      {/* Help Section */}
      <section className="bg-gray-50 rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h3>

        {/* IoT Gateway Section */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-3">IoT Gateway (wg0) - 1:1 Peer Model</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-2xl mb-2">1️⃣</div>
              <h5 className="font-medium text-gray-900 mb-1">Enable WireGuard</h5>
              <p className="text-sm text-gray-600">
                Configure the WireGuard server on this edge device with a tunnel network and public endpoint.
              </p>
            </div>
            <div>
              <div className="text-2xl mb-2">2️⃣</div>
              <h5 className="font-medium text-gray-900 mb-1">Add IoT Gateways</h5>
              <p className="text-sm text-gray-600">
                Add each GL.iNet Opal or other WireGuard-capable gateway as a peer with its public key.
              </p>
            </div>
            <div>
              <div className="text-2xl mb-2">3️⃣</div>
              <h5 className="font-medium text-gray-900 mb-1">Configure Gateways</h5>
              <p className="text-sm text-gray-600">
                Export the config and enter it on each gateway. They'll connect automatically using split-tunnel.
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-white rounded border border-gray-200">
            <h5 className="font-medium text-gray-900 mb-1 text-sm">Split Tunnel Configuration</h5>
            <p className="text-sm text-gray-600">
              By default, only traffic to specified networks (like 10.0.0.0/8, 192.168.0.0/16) routes through
              the VPN tunnel. Internet traffic from the IoT gateway goes directly through its local connection.
            </p>
          </div>
        </div>

        {/* App Server Section */}
        <div className="pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">App Server (wg1) - Many:1 Peer Model</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-2xl mb-2">1️⃣</div>
              <h5 className="font-medium text-gray-900 mb-1">Enable App Server</h5>
              <p className="text-sm text-gray-600">
                Enable the WireGuard app server to allow mobile and desktop VPN clients to connect.
              </p>
            </div>
            <div>
              <div className="text-2xl mb-2">2️⃣</div>
              <h5 className="font-medium text-gray-900 mb-1">Automatic Registration</h5>
              <p className="text-sm text-gray-600">
                Mobile/desktop apps automatically register and get assigned to this edge with load balancing.
              </p>
            </div>
            <div>
              <div className="text-2xl mb-2">3️⃣</div>
              <h5 className="font-medium text-gray-900 mb-1">Full Tunnel VPN</h5>
              <p className="text-sm text-gray-600">
                App clients use full tunnel (0.0.0.0/0) by default, routing all traffic through the VPN.
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-white rounded border border-gray-200">
            <h5 className="font-medium text-gray-900 mb-1 text-sm">VRF Isolation</h5>
            <p className="text-sm text-gray-600">
              wg0 (IoT Gateway) uses VRF 1 on port 51820, while wg1 (App Server) uses VRF 2 on port 51821.
              This provides complete network isolation between IoT gateways and app clients.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WireGuard;
